import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";

import { UseHandleKanban } from "0.Mine/1.Hooks/CRM/Kanban";
import { UseHandleLeads } from "0.Mine/1.Hooks/CRM/Leads";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { Lead, LeadState } from "0.Mine/3.Interfaces/lead";

import { ActivityNote, FormData, NewNote } from "../sharedTypes";

export const useTaskModalState = (onClose: () => void, cardId: string, lead?: Lead) => {
  const [tabValue, setTabValue] = useState(0);
  const { createActivityNote, getAllCardActivityNotes, getCardActivityAsFormData } =
    UseHandleKanban();
  const { createLeadCardInformation } = UseHandleLeads();
  const { session } = useBusinessContext();

  const [formData, setFormData] = useState<FormData>({
    name: lead?.name || "",
    company: lead?.company || "",
    email: lead?.email || "",
    phone: lead?.phone || "",
    lastContact: lead?.lastContact || new Date(),
    value: lead?.value || "",
    notes: lead?.notes || "",
    state: lead?.state || LeadState.New,
    leadSource: lead?.leadSource || "",
    dueDate: lead?.dueDate || new Date(),
    statusId: lead?.status?.id || lead?.state || "",
    probability: lead?.probability || 0,
    vendorId: lead?.vendor_id || "",
    vendorName: lead?.vendor_name || "",
    address: "",
    city: "",
  });

  const [activities, setActivities] = useState<ActivityNote[]>([]);

  const [showAddNoteForm, setShowAddNoteForm] = useState(false);
  const [showAllActivities, setShowAllActivities] = useState(false);

  const [newNote, setNewNote] = useState<NewNote>({
    title: "",
    description: "",
    type: "note",
  });

  useEffect(() => {
    const fetchData = async () => {
      if (session && session.selectedBusiness) {
        const activities = await getAllCardActivityNotes(
          session.selectedBusiness.id,
          Number(cardId)
        );
        if (activities) {
          setActivities(activities);
        }

        if (!lead) {
          const data = await getCardActivityAsFormData(session.selectedBusiness.id, Number(cardId));
          if (data) {
            setFormData(data);
          }
        }
      }
    };

    fetchData();
  }, [session, cardId, lead]);

  useEffect(() => {
    if (lead) {
      setFormData({
        name: lead.name || "",
        company: lead.company || "",
        email: lead.email || "",
        phone: lead.phone || "",
        lastContact: lead.lastContact || new Date(),
        value: lead.value || "",
        notes: lead.notes || "",
        state: lead.state || LeadState.New,
        leadSource: lead.leadSource || "",
        dueDate: lead.dueDate || new Date(),
        statusId: lead.status?.id || lead.state || "",
        probability: lead.probability || 0,
        vendorId: lead.vendor_id || "",
        vendorName: lead.vendor_name || "",
        address: "",
        city: "",
      });
    }
  }, [lead]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date: Date | null) => {
    setFormData((prev) => ({ ...prev, lastContact: date || new Date() }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    try {
      e.preventDefault();
      console.log("Form submitted:", formData);
      const response = await createLeadCardInformation(
        session.selectedBusiness.id,
        cardId,
        formData
      );
      console.log("RESPONSE IS : ", response);
      enqueueSnackbar("Lead aggiornato con successo", { variant: "success" });
      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
      enqueueSnackbar("Errore durante l'aggiornamento del lead", { variant: "error" });
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleNoteInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewNote((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddNote = async () => {
    if (newNote.title.trim() === "" || newNote.description.trim() === "") {
      return;
    }

    const newActivity: ActivityNote = {
      id: Date.now().toString(),
      title: newNote.title,
      description: newNote.description,
      date: new Date(),
      type: newNote.type,
      user_name: newNote?.user_name,
      created_at: newNote.created_at ? newNote.created_at : new Date(),
    };

    setActivities((prev) => [newActivity, ...prev]);
    setNewNote({ title: "", description: "", type: "note" });
    setShowAddNoteForm(false);
    if (session && session.selectedBusiness) {
      const activityId = await createActivityNote(
        session.selectedBusiness.id,
        Number(cardId),
        newActivity
      );
      setActivities((prev) =>
        prev.map((activity) =>
          activity.id === newActivity.id ? { ...activity, id: activityId } : activity
        )
      );
    }
  };

  return {
    tabValue,
    formData,
    activities,
    showAddNoteForm,
    showAllActivities,
    newNote,
    setTabValue,
    setFormData,
    setActivities,
    setShowAddNoteForm,
    setShowAllActivities,
    setNewNote,
    handleInputChange,
    handleSelectChange,
    handleDateChange,
    handleSubmit,
    handleTabChange,
    handleNoteInputChange,
    handleAddNote,
  };
};
